import { computed, type Ref } from 'vue'

import { useContext } from '@/composables/context'
import {
  useCreateTransportationGroup,
  useTransportationReuseEnabled,
  useUpdateTransportationGroup,
} from '@/composables/transportation-group'
import { useSelectedTrip } from '@/composables/trip'
import { eventModule } from '@/store'
import { useTransportationStore } from '@/stores/transportation'
import { useTransportationGroupStore } from '@/stores/transportation-group'
import type { TransportationUIInput } from '@/types/transportation'
import {
  buildGroupFromWaypointIds,
  demotesToStandalone,
  effectiveAnchorEnabled,
  effectiveGroupLegCount,
  fallbackRegularWaypointIds,
  regularFromWaypointIds,
} from '@/utils/transportation-group-sync'

export function useTransportationGroupSync(params: {
  transportation: Ref<TransportationUIInput | undefined>
  waypointId: Ref<string | undefined>
}) {
  const { transportation, waypointId } = params

  const trip = useSelectedTrip()
  const tripId = computed(() => trip.value._id)
  const reuseEnabled = useTransportationReuseEnabled()
  const { $sentry } = useContext()
  const transportationStore = useTransportationStore()
  const groupStore = useTransportationGroupStore()
  const { mutateAsync: createGroupMutation } =
    useCreateTransportationGroup(tripId)
  const { mutateAsync: updateGroupMutation } =
    useUpdateTransportationGroup(tripId)

  const lastWaypointId = computed(() => {
    const wps = trip.value?.waypoints ?? []
    return wps[wps.length - 1]?._id
  })

  const currentGroup = computed(() =>
    transportation.value
      ? groupStore.getGroupForTransportation(transportation.value)
      : undefined,
  )

  const seedIsArrival = computed(
    () => !!transportation.value?.isArrivalTransportation,
  )

  const seedIsDeparture = computed(
    () =>
      !seedIsArrival.value &&
      !!waypointId.value &&
      waypointId.value === lastWaypointId.value,
  )

  function groupHasDeparture(
    group: ReturnType<typeof groupStore.getGroupForTransportation>,
  ) {
    return group ? groupStore.hasDepartureTransportation(group) : false
  }

  function fallbackParams(
    existingGroup: ReturnType<typeof groupStore.getGroupForTransportation>,
  ) {
    return {
      persistedFromWaypointIds: existingGroup?.fromWaypointIds ?? [],
      hasExistingGroup: !!existingGroup,
      lastWaypointId: lastWaypointId.value,
      waypointId: waypointId.value,
      seedIsArrival: seedIsArrival.value,
      seedIsDeparture: seedIsDeparture.value,
    }
  }

  function diffSelection(): {
    changed: boolean
    willDemote: boolean
    newCount: number
  } {
    if (!reuseEnabled.value) {
      return { changed: false, willDemote: false, newCount: 0 }
    }
    const sel = transportationStore.selectedTransportation
    const selDraft = sel?.groupSelection
    const arrivalDraft = sel?.groupArrival
    const departureDraft = sel?.groupDeparture
    const existingGroup = transportation.value
      ? groupStore.getGroupForTransportation(transportation.value)
      : undefined

    const existingRegularIds = regularFromWaypointIds(
      existingGroup?.fromWaypointIds ?? [],
      lastWaypointId.value,
    )
    const effectiveRegularIds = selDraft?.touched
      ? regularFromWaypointIds(selDraft.value, lastWaypointId.value)
      : fallbackRegularWaypointIds(fallbackParams(existingGroup))
    const arrivalEnabled = effectiveAnchorEnabled(
      arrivalDraft,
      seedIsArrival.value || existingGroup?.hasArrivalTransportation,
    )
    const departureEnabled = effectiveAnchorEnabled(
      departureDraft,
      seedIsDeparture.value || groupHasDeparture(existingGroup),
    )

    const existingIds = new Set(existingRegularIds)
    const draftSet = new Set(effectiveRegularIds)
    const sameSize = existingIds.size === draftSet.size
    const idsUnchanged =
      sameSize && [...existingIds].every((id) => draftSet.has(id))
    const arrivalUnchanged =
      arrivalEnabled === !!existingGroup?.hasArrivalTransportation
    const departureUnchanged =
      departureEnabled === groupHasDeparture(existingGroup)
    const changed = !idsUnchanged || !arrivalUnchanged || !departureUnchanged

    const effectiveCount = effectiveGroupLegCount({
      regularLegCount: draftSet.size,
      arrivalEnabled,
      departureEnabled,
    })

    return {
      changed,
      willDemote: !!existingGroup && demotesToStandalone(effectiveCount),
      newCount: effectiveCount,
    }
  }

  async function syncGroupSelection(transportationId: string) {
    if (!reuseEnabled.value || !transportation.value) {
      return
    }
    const sel = transportationStore.selectedTransportation
    const selDraft = sel?.groupSelection
    const arrivalDraft = sel?.groupArrival
    const departureDraft = sel?.groupDeparture
    if (
      !selDraft?.touched &&
      !arrivalDraft?.touched &&
      !departureDraft?.touched
    ) {
      return
    }

    const existingGroup = groupStore.getGroupForTransportation({
      ...transportation.value,
      _id: transportationId,
    })

    // The transportation reports a groupId but the store has no matching group
    // — likely a failed list query left the store empty. Refuse to proceed
    // rather than creating a duplicate group on the server.
    if (transportation.value.groupId && !existingGroup) {
      $sentry.captureMessage(
        'Transportation references missing group in store',
        {
          level: 'error',
          extra: {
            transportationId,
            groupId: transportation.value.groupId,
            tripId: tripId.value,
          },
        },
      )
      eventModule.newError('error.transportation.fetch')
      throw new Error('Transportation references missing group in store')
    }

    const effectiveRegularIds = selDraft?.touched
      ? regularFromWaypointIds(selDraft.value, lastWaypointId.value)
      : fallbackRegularWaypointIds(fallbackParams(existingGroup))
    const arrivalEnabled = effectiveAnchorEnabled(
      arrivalDraft,
      seedIsArrival.value || existingGroup?.hasArrivalTransportation,
    )
    const departureEnabled = effectiveAnchorEnabled(
      departureDraft,
      seedIsDeparture.value || groupHasDeparture(existingGroup),
    )

    if (existingGroup) {
      if (!diffSelection().changed) {
        return
      }
      const input: Parameters<typeof updateGroupMutation>[0]['input'] = {}
      if (selDraft?.touched && !departureDraft?.touched) {
        input.fromWaypointIds = buildGroupFromWaypointIds(
          selDraft.value,
          groupHasDeparture(existingGroup),
          lastWaypointId.value,
        )
      } else if (selDraft?.touched || departureDraft?.touched) {
        input.fromWaypointIds = buildGroupFromWaypointIds(
          effectiveRegularIds,
          departureEnabled,
          lastWaypointId.value,
        )
      }
      if (
        arrivalDraft?.touched &&
        arrivalDraft.value.enabled !== existingGroup.hasArrivalTransportation
      ) {
        input.hasArrivalTransportation = arrivalDraft.value.enabled
        if (
          arrivalDraft.value.enabled &&
          !seedIsArrival.value &&
          arrivalDraft.value.location?.location
        ) {
          input.originLocation = arrivalDraft.value.location.location
        }
      }
      if (departureDraft?.touched) {
        const existingDeparture = groupHasDeparture(existingGroup)
        if (
          departureDraft.value.enabled &&
          !existingDeparture &&
          !seedIsDeparture.value &&
          departureDraft.value.location?.location
        ) {
          input.destinationLocation = departureDraft.value.location.location
        }
      }
      await updateGroupMutation(
        { groupId: existingGroup._id, input },
        {
          onError: () => {
            eventModule.newError('error.transportation.groupSave')
          },
        },
      )
      return
    }

    const effectiveCount = effectiveGroupLegCount({
      regularLegCount: effectiveRegularIds.length,
      arrivalEnabled,
      departureEnabled,
    })
    if (demotesToStandalone(effectiveCount)) {
      return
    }
    const needsArrivalOrigin = arrivalEnabled && !seedIsArrival.value
    const needsDepartureDestination = departureEnabled && !seedIsDeparture.value
    const arrivalLocation = arrivalDraft?.touched
      ? arrivalDraft.value.location?.location
      : undefined
    const departureLocation = departureDraft?.touched
      ? departureDraft.value.location?.location
      : undefined

    await createGroupMutation(
      {
        type: 'driving',
        fromWaypointIds: buildGroupFromWaypointIds(
          effectiveRegularIds,
          departureEnabled,
          lastWaypointId.value,
        ),
        seedTransportationId: transportationId,
        hasArrivalTransportation: arrivalEnabled || undefined,
        originLocation: needsArrivalOrigin ? arrivalLocation : undefined,
        destinationLocation: needsDepartureDestination
          ? departureLocation
          : undefined,
      },
      {
        onError: () => {
          eventModule.newError('error.transportation.groupSave')
        },
      },
    )
  }

  return {
    currentGroup,
    seedIsArrival,
    seedIsDeparture,
    diffSelection,
    syncGroupSelection,
  }
}
